import React from "react";
import PropTypes from "prop-types";
import {Col, OverlayTrigger, Row, Spinner, Tooltip} from "react-bootstrap";
import Modals from "../../../../components/modals";
import {formatDate} from "../../../../utils/util";

const componentName = "noShow";

const NoShowModal = ({
  show,
  onHide,
  onSubmit,
  patient,
  visits,
  selectedCareGapIds,
  updateSelection,
  loading,
  submitting,
  error,
  submitError,
  size = "md",
}) => {
  const appointmentDateDisplay = formatDate(
    patient?.patientGapAppointmentDate || patient?.appointmentDate || patient?.appointmentDateTime || null
  );
  const hasMultipleVisits = Array.isArray(visits) && visits.length > 1;
  const idPrefix = `${componentName}-noShow`;

  return (
    <Modals
      centered
      show={show}
      onHide={onHide}
      size={size}
      title="No Show"
      titleid={`${componentName}-NoShowModal`}
      body={
        <div>
          <Row className="mb-3">
            <Col>
              <span id={`${idPrefix}-patientNameLabel`} className="fw-bold d-block">
                Patient Name
              </span>
              <span id={`${idPrefix}-patientNameValue`} className="forced-capital d-block">
                {patient?.fullName}
              </span>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <p>
                {hasMultipleVisits
                  ? `We found multiple visits scheduled for this patient on ${appointmentDateDisplay}. Please select one or more visits that you would like to mark as No Show`
                  : `We found one visit scheduled for this patient on ${appointmentDateDisplay}. Would you like to mark this visit as a No Show?`}
              </p>
            </Col>
          </Row>

          {error && (
            <Row className="mb-3">
              <Col>
                <div className="text-danger">{error}</div>
              </Col>
            </Row>
          )}

          {submitError && (
            <Row className="mb-3">
              <Col>
                <div className="text-danger">{submitError}</div>
              </Col>
            </Row>
          )}

          {loading ? (
            <div className="text-center py-4">
              <output aria-live="polite">
                <Spinner animation="border" />
                <span className="visually-hidden">Loading...</span>
              </output>
            </div>
          ) : (
            <Row className="mb-3">
              <Col>
                <span id={`${idPrefix}-scheduledVisitsLabel`} className="fw-bold d-block">
                  Scheduled Visits
                </span>
                <div className="d-flex flex-column gap-2 mt-2">
                  {visits?.length ? (
                    visits.map((visit, index) => {
                      const visitKey = `${visit?.careGapId || "visit"}-${index}`;
                      const visitCareGapId = visit?.careGapId || null;
                      const showCheckbox = hasMultipleVisits && !!visitCareGapId;
                      const postpartumPrefix = "Follow-Up after Child Birth";
                      const visitDescription = visit?.description || visit?.gapDescription || "Scheduled visit";
                      const isPostpartumVisit =
                        (visit?.description || "").startsWith(postpartumPrefix) ||
                        (visit?.gapDescription || "").startsWith(postpartumPrefix);
                      const checkboxId = showCheckbox ? `${idPrefix}-visit-${visitKey}` : null;
                      const isSelected = visitCareGapId ? selectedCareGapIds.includes(visitCareGapId) : false;
                      const infoWrapperClass = showCheckbox ? "flex-grow-1 ms-2" : "flex-grow-1";
                      const content = (
                        <span className="d-block">
                          {isPostpartumVisit ? (
                            <OverlayTrigger
                              placement="auto"
                              overlay={(
                                <Tooltip id={`${componentName}-postpartumTooltip-${visitKey}`}>
                                  Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
                                </Tooltip>
                              )}
                            >
                              <span>{visitDescription}</span>
                            </OverlayTrigger>
                          ) : (
                            visitDescription
                          )}
                        </span>
                      );

                      return (
                        <div key={visitKey} className="d-flex align-items-start rounded p-2">
                          {showCheckbox && (
                            <div className="form-check m-0">
                              <input
                                id={checkboxId}
                                className="form-check-input mt-1"
                                type="checkbox"
                                checked={isSelected}
                                onChange={(event) => updateSelection(visitCareGapId, event.target.checked)}
                              />
                            </div>
                          )}
                          <div className={infoWrapperClass}>
                            {showCheckbox ? (
                              <label className="form-check-label d-block" htmlFor={checkboxId}>
                                {content}
                              </label>
                            ) : (
                              content
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted fst-italic">No scheduled visits found.</div>
                  )}
                </div>
              </Col>
            </Row>
          )}

          {submitting && !loading && (
            <Row className="mb-2">
              <Col>
                <small className="text-muted">Submitting updates...</small>
              </Col>
            </Row>
          )}
        </div>
      }
      completeBtn="Submit"
      completeBtnId={`${componentName}-NoShowSubmit`}
      closeBtn="Cancel"
      closeBtnId={`${componentName}-NoShowCancel`}
      onCreate={onSubmit}
    />
  );
};

NoShowModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  patient: PropTypes.object,
  visits: PropTypes.arrayOf(PropTypes.object),
  selectedCareGapIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  updateSelection: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  submitting: PropTypes.bool,
  error: PropTypes.string,
  submitError: PropTypes.string,
  size: PropTypes.string,
};

NoShowModal.defaultProps = {
  patient: null,
  visits: [],
  selectedCareGapIds: [],
  loading: false,
  submitting: false,
  error: null,
  submitError: null,
  size: "md",
};

export default NoShowModal;
